export type PosSyncScenario = 'readOnlySync' | 'syncInPlace' | 'syncAndPublish';

export function getPosSyncScenario(
  isPosSyncInPlace: boolean,
  isPosAutoPublishOnSync: boolean,
): PosSyncScenario {
  if (!isPosSyncInPlace) return 'readOnlySync';
  return isPosAutoPublishOnSync ? 'syncAndPublish' : 'syncInPlace';
}

// Defaults to the plural form when the count is unknown (e.g. menus not loaded yet).
const menuNoun = (menuCount?: number) => (menuCount === 1 ? 'menu' : 'menus');
const menuNounTitle = (menuCount?: number) => (menuCount === 1 ? 'Menu' : 'Menus');

type PosSyncBodyLine = {
  text: string;
  strong?: boolean;
};

type PosSyncCopyEntry = {
  getButtonLabel: (menuCount?: number) => string;
  getModalTitle: (menuCount?: number) => string;
  confirmLabel: string;
  getBody: (readableAppName: string, menuCount?: number) => PosSyncBodyLine[];
  getSuccessMessage: (readableAppName: string, formattedDate: string) => string;
};

/* Single source of truth for the POS sync/publish button label, confirmation
modal copy, and success notification, across all three POS Resource flag
combinations (mm_sync_in_place / mm_auto_publish). */
export const POS_SYNC_COPY: Record<PosSyncScenario, PosSyncCopyEntry> = {
  readOnlySync: {
    getButtonLabel: () => 'Refresh POS',
    getModalTitle: () => 'POS Menu Refresh',
    confirmLabel: 'Resume',
    getBody: (readableAppName, menuCount) => [
      {
        text: `This action will replace your read-only POS menu from ${readableAppName} with the latest version. It will not have an effect on your current app ${menuNoun(menuCount)}.`,
      },
      {
        text: `New/updated items (categories, items, modifier groups, and modifiers) in your ${readableAppName} menu will be available for selection in your publishable app ${menuNoun(menuCount)}.`,
      },
    ],
    getSuccessMessage: readableAppName => `Read-Only Sync request sent for ${readableAppName}`,
  },
  syncInPlace: {
    getButtonLabel: menuCount => `Sync POS ${menuNounTitle(menuCount)}`,
    getModalTitle: menuCount => `Sync POS ${menuNounTitle(menuCount)}`,
    confirmLabel: 'Confirm',
    getBody: (_readableAppName, menuCount) => [
      {
        text: `This will import any and all changes in your POS ${menuNoun(menuCount)}, ready for you to review and publish to your live ordering ${menuNoun(menuCount)}.`,
      },
    ],
    getSuccessMessage: readableAppName => `POS Menu Sync is in Progress for ${readableAppName}`,
  },
  syncAndPublish: {
    getButtonLabel: menuCount => `Sync & Publish POS ${menuNounTitle(menuCount)}`,
    getModalTitle: menuCount => `Sync & Publish POS ${menuNounTitle(menuCount)}`,
    confirmLabel: 'Confirm',
    getBody: (_readableAppName, menuCount) => [
      {
        text: `This will import any and all changes in your POS ${menuNoun(menuCount)} and immediately publish them to your live ordering ${menuNoun(menuCount)}.`,
      },
    ],
    getSuccessMessage: readableAppName =>
      `Menu Sync is in Progress for ${readableAppName}, Publish will follow after completion`,
  },
};
